import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAttorneyGeneralWizardContext } from '../../../hooks/useCaseRequestWizard';
import { CONTACT_NUMBER_LENGTH, contactNumberError, exceedsMax } from '../../../utils/caseFieldValidation';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors } from '../../../utils/requiredValidation';
import { SectionCard } from '../WizardComponents';
import { AttorneyGeneralWizardActions } from './AttorneyGeneralWizardActions';
import { attorneyGeneralWizardPath, AttorneyGeneralWizardShell, FieldError, inpClass } from './AttorneyGeneralWizardShell';
import { agEnterPlaceholder, agSelectPlaceholder } from './attorneyGeneralPlaceholders';
import { validateAttorneyGeneralStep } from './attorneyGeneralStepValidation';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_NAME = 255;
const MAX_DESC = 2000;
const MAX_CONTACT = CONTACT_NUMBER_LENGTH;

export default function AttorneyGeneralStep2PartyDetailsPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useAttorneyGeneralWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateAttorneyGeneralStep(2, draft);
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  return (
    <AttorneyGeneralWizardShell currentStep={2} draft={draft}>
      <SectionCard title={f.sectionPartyDetails}>
        <div className="ccr-fg" id="ag-partyBodyType" style={{ marginBottom: 14 }}>
          <label>
            Party Body Type <span className="req">*</span>
          </label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="partyBodyType"
                checked={draft.partyBodyType === 'government'}
                onChange={() => {
                  setDraft((d) => ({ ...d, partyBodyType: 'government' }));
                  clearErr('partyBodyType');
                }}
              />
              <span>Government Ministry/ Department/Other Unincorporated Body</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="partyBodyType"
                checked={draft.partyBodyType === 'incorporated'}
                onChange={() => {
                  setDraft((d) => ({ ...d, partyBodyType: 'incorporated' }));
                  clearErr('partyBodyType');
                }}
              />
              <span>Public Corporation/Statutory Board/State Owned Entity/Other Incorporated Body</span>
            </label>
          </div>
          <FieldError message={errors.partyBodyType} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.plaintiffApplicantName}</label>
            <input
              id="ag-plaintiff"
              className={inpClass(Boolean(errors.plaintiffName))}
              type="text"              value={draft.plaintiffName}
              onChange={(e) => {
                setDraft((d) => ({ ...d, plaintiffName: maxLen('plaintiffName', e.target.value, MAX_NAME) }));
              }}
              placeholder={agEnterPlaceholder('Plaintiff / Applicant Name')}
            />
            <FieldError message={errors.plaintiffName} />
          </div>
          <div className="ccr-fg">
            <label>{f.defendantRespondentName}</label>
            <input
              id="ag-defendant"
              className={inpClass(Boolean(errors.defendantName))}
              type="text"              value={draft.defendantName}
              onChange={(e) => {
                setDraft((d) => ({ ...d, defendantName: maxLen('defendantName', e.target.value, MAX_NAME) }));
              }}
              placeholder={agEnterPlaceholder('Defendant / Respondent Name')}
            />
            <FieldError message={errors.defendantName} />
          </div>
        </div>

        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>{f.otherInvolvedInstitutions}</label>
          <textarea
            className={inpClass(Boolean(errors.otherInvolvedInstitutions))}
            rows={3}            value={draft.otherInvolvedInstitutions}
            onChange={(e) =>
              setDraft((d) => ({ ...d, otherInvolvedInstitutions: maxLen('otherInvolvedInstitutions', e.target.value, MAX_DESC) }))
            }
            placeholder={agEnterPlaceholder('Other Involved Institutions')}
          />
          <FieldError message={errors.otherInvolvedInstitutions} />
        </div>

        <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
          <div className="ccr-fg">
            <label>{f.legalOfficerNameAlt}</label>
            <input
              id="ag-legalOfficer"
              className={inpClass(Boolean(errors.legalOfficerName))}
              type="text"              value={draft.legalOfficerName}
              onChange={(e) => {
                setDraft((d) => ({ ...d, legalOfficerName: maxLen('legalOfficerName', e.target.value, MAX_NAME) }));
              }}
              placeholder={agEnterPlaceholder('Name of the Legal Officer/ Officer In-Charge')}
            />
            <FieldError message={errors.legalOfficerName} />
          </div>
          <div className="ccr-fg">
            <label>{f.contactNumber}</label>
            <input
              id="ag-legalContact"
              className={inpClass(Boolean(errors.legalOfficerContact))}
              type="text"
              inputMode="numeric"              value={draft.legalOfficerContact}
              onChange={(e) => {
                setDraft((d) => ({
                  ...d,
                  legalOfficerContact: e.target.value.replace(/\D/g, '').slice(0, MAX_CONTACT) }));
                clearErr('legalOfficerContact');
              }}
              onBlur={() => {
                const err = contactNumberError(draft.legalOfficerContact);
                if (err) setErrors((e) => ({ ...e, legalOfficerContact: err }));
              }}
              placeholder={agEnterPlaceholder('Contact Number')}
            />
            <FieldError message={errors.legalOfficerContact} />
          </div>
        </div>
      </SectionCard>

      <AttorneyGeneralWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={2}
        onBack={() => nav(attorneyGeneralWizardPath(1, draft.draftRequestId))}
        onNext={() => {
          if (validate()) nav(attorneyGeneralWizardPath(3, draft.draftRequestId));
        }}
        validateBeforeSave={validate}
      />
    </AttorneyGeneralWizardShell>
  );
}
